import { proposalInputSchema, sekToOre, type ProposalInput, type ProviderProposal } from '@hemmafixa/shared';
import { Button, FilterChips, FormField, Input, Textarea, toast } from '@hemmafixa/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useI18n } from '@/i18n/context';
import type { Messages } from '@/i18n/sv';
import { ApiError } from '@/lib/api';

type FieldKey = keyof Messages['provider']['job']['errors'];
type PriceType = 'fixed' | 'range';

interface Values {
  priceType: PriceType;
  priceFrom: string;
  priceTo: string;
  includes: string;
  earliestStart: string;
  durationDays: string;
  message: string;
}

const kronor = (ore: number | null) => (ore === null ? '' : String(ore / 100));

function initialValues(proposal?: ProviderProposal): Values {
  return {
    priceType: proposal?.priceToOre ? 'range' : 'fixed',
    priceFrom: kronor(proposal?.priceFromOre ?? null),
    priceTo: kronor(proposal?.priceToOre ?? null),
    includes: proposal?.includes ?? '',
    earliestStart: proposal?.earliestStart ?? '',
    durationDays: proposal?.durationDays ? String(proposal.durationDays) : '',
    message: proposal?.message ?? '',
  };
}

/** "12 500" or "12500,50" → öre; NaN when it is not a number, so the schema reports it */
function toOre(text: string) {
  const number = Number(text.replace(/\s/g, '').replace(',', '.'));
  return text.trim() === '' || !Number.isFinite(number) ? Number.NaN : sekToOre(number);
}

function toInput(values: Values): ProposalInput {
  return {
    priceFromOre: toOre(values.priceFrom),
    priceToOre: values.priceType === 'range' ? toOre(values.priceTo) : null,
    includes: values.includes,
    earliestStart: values.earliestStart || null,
    durationDays: values.durationDays ? Number(values.durationDays) : null,
    message: values.message,
  };
}

/**
 * Sends a new proposal, or changes one that is still submitted (P4-06). Checked with the same schema as the API;
 * prices are typed in kronor and sent in öre.
 */
export function ProposalForm({
  proposal,
  mutation,
  onDone,
  onCancel,
}: {
  proposal?: ProviderProposal;
  mutation: UseMutationResult<ProviderProposal, Error, ProposalInput>;
  onDone?: () => void;
  onCancel?: () => void;
}) {
  const { t } = useI18n();
  const j = t.provider.job;
  const [values, setValues] = useState(() => initialValues(proposal));
  const [errors, setErrors] = useState<Partial<Record<FieldKey, string>>>({});
  const set = (key: keyof Values) => (value: string) =>
    setValues((current) => ({ ...current, [key]: value }));
  const fieldError = (key: FieldKey) => errors[key];

  const showErrors = (paths: string[]) =>
    setErrors(
      Object.fromEntries(
        paths.filter((path): path is FieldKey => path in j.errors).map((path) => [path, j.errors[path]]),
      ),
    );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const input = toInput(values);
    const parsed = proposalInputSchema.safeParse(input);
    if (!parsed.success) {
      showErrors(parsed.error.issues.map((issue) => String(issue.path[0])));
      return;
    }
    setErrors({});
    mutation.mutate(input, {
      onSuccess: () => {
        toast.success(proposal ? j.savedToast : j.sentToast);
        onDone?.();
      },
      onError: (error) => {
        if (error instanceof ApiError && error.fields?.length) {
          showErrors(error.fields.map((field) => field.path.split('.')[0] ?? ''));
        } else if (error instanceof ApiError && error.code === 'CONFLICT') {
          toast.error(j.conflict);
        } else {
          toast.error(j.failed);
        }
      },
    });
  };

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
      <div className="sm:col-span-2">
        <FilterChips
          label={j.priceType}
          value={values.priceType}
          onChange={(value) => set('priceType')(value)}
          options={[
            { value: 'fixed' as const, label: j.fixedPrice },
            { value: 'range' as const, label: j.priceRange },
          ]}
        />
      </div>
      <FormField
        label={values.priceType === 'range' ? j.priceFrom : j.price}
        hint={j.priceHint}
        error={fieldError('priceFromOre')}
        required
      >
        <Input
          inputMode="decimal"
          autoComplete="off"
          value={values.priceFrom}
          onChange={(event) => set('priceFrom')(event.target.value)}
        />
      </FormField>
      {values.priceType === 'range' ? (
        <FormField label={j.priceTo} error={fieldError('priceToOre')} required>
          <Input
            inputMode="decimal"
            autoComplete="off"
            value={values.priceTo}
            onChange={(event) => set('priceTo')(event.target.value)}
          />
        </FormField>
      ) : (
        <div className="hidden sm:block" />
      )}
      <FormField label={j.start} error={fieldError('earliestStart')}>
        <Input
          type="date"
          value={values.earliestStart}
          onChange={(event) => set('earliestStart')(event.target.value)}
        />
      </FormField>
      <FormField label={j.duration} error={fieldError('durationDays')}>
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          max={365}
          value={values.durationDays}
          onChange={(event) => set('durationDays')(event.target.value)}
        />
      </FormField>
      <FormField
        label={j.includes}
        hint={j.includesHint}
        error={fieldError('includes')}
        className="sm:col-span-2"
      >
        <Input
          maxLength={1000}
          value={values.includes}
          onChange={(event) => set('includes')(event.target.value)}
        />
      </FormField>
      <FormField
        label={j.message}
        hint={j.messageHint}
        error={fieldError('message')}
        required
        className="sm:col-span-2"
      >
        <Textarea
          rows={5}
          maxLength={2000}
          value={values.message}
          onChange={(event) => set('message')(event.target.value)}
        />
      </FormField>
      <div className="flex flex-wrap justify-end gap-3 sm:col-span-2">
        {onCancel && (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t.common.cancel}
          </Button>
        )}
        <Button type="submit" loading={mutation.isPending}>
          {proposal ? j.saveChanges : j.send}
        </Button>
      </div>
    </form>
  );
}
